import { getI18n } from '@bridebook/web/lib/i18n/getI18n';

export type FacetFilterKey =
  | 'highestReviewed'
  | 'asian'
  | 'barn'
  | 'castle'
  | 'exclusiveUse'
  | 'pricingAffordable'
  | 'affordable'
  | 'luxury'
  | 'pricingLuxury'
  | 'intimate'
  | 'gardenOutdoor'
  | 'hotel'
  | 'unusual'
  | 'pubRestaurant'
  | 'historic'
  | 'modern'
  | 'romantic'
  | 'rustic'
  | 'videographyAvailable'
  | 'weddingLicence'
  | 'conferenceCentre'
  | 'cruiseBoatYacht'
  | 'townHallRegistryOffice'
  | 'placeOfWorship'
  | 'outdoor'
  | 'countryHouseManorHouse'
  | 'statelyHome'
  | 'hasLateAvailability'
  | 'musicDj'
  | 'musicBand'
  | 'musicSolo'
  | 'musicClassic'
  | 'cateringStaffProvided'
  | 'cateringTablewareProvided'
  | 'resort'
  | 'uniqueVenueType';

export type QuickFilterKey = 'ourVenue' | 'countryHouse' | 'weddingLicence' | 'outdoorSpace';

type FacetFilters = {
  label: string;
  key: FacetFilterKey;
};

export const quickFiltersEmojis: Record<FacetFilterKey | QuickFilterKey, string> = {
  ourVenue: '',
  highestReviewed: '🌟',
  affordable: '💰',
  asian: '🌏',
  barn: '🌾',
  castle: '🏰',
  exclusiveUse: '✨',
  gardenOutdoor: '🌿',
  historic: '👑',
  hotel: '🏨',
  intimate: '💞',
  luxury: '💫',
  modern: '🎊',
  pricingAffordable: '💰',
  pricingLuxury: '🤩',
  pubRestaurant: '🍴',
  romantic: '❤️',
  rustic: '🍂',
  unusual: '💡',
  weddingLicence: '💍',
  countryHouse: '🏡',
  countryHouseManorHouse: '🏡',
  outdoorSpace: '🌿',
  videographyAvailable: '🎥',
  conferenceCentre: '🏛',
  cruiseBoatYacht: '🛳️',
  townHallRegistryOffice: '🖋️',
  placeOfWorship: '🙏',
  outdoor: '🌿',
  statelyHome: '🏛',
  hasLateAvailability: '🗓️',
  musicDj: '🎧',
  musicBand: '🎤',
  musicSolo: '🎵',
  musicClassic: '🎻',
  cateringStaffProvided: '👩‍🍳',
  cateringTablewareProvided: '🍽️',
  resort: '🏖️',
  uniqueVenueType: '✨',
};

const getFacetFiltersTexts = (isInText: boolean): Record<FacetFilterKey, string> => {
  const i18n = getI18n();

  const defaultTexts = {
    highestReviewed: i18n.t('search:facets.highestReviewed'),
    affordable: i18n.t('search:facets.affordable'),
    asian: i18n.t('search:facets.asian'),
    barn: i18n.t('search:facets.barn'),
    castle: i18n.t('search:facets.castle'),
    exclusiveUse: i18n.t('search:facets.exclusiveUse'),
    gardenOutdoor: i18n.t('search:facets.gardenOutdoor'),
    historic: i18n.t('search:facets.historic'),
    hotel: i18n.t('search:facets.hotel'),
    intimate: i18n.t('search:facets.intimate'),
    luxury: i18n.t('search:facets.luxury'),
    modern: i18n.t('search:facets.modern'),
    pricingAffordable: i18n.t('search:facets.pricingAffordable'),
    pricingLuxury: i18n.t('search:facets.pricingLuxury'),
    pubRestaurant: i18n.t('search:facets.pubRestaurant'),
    romantic: i18n.t('search:facets.romantic'),
    rustic: i18n.t('search:facets.rustic'),
    unusual: i18n.t('search:facets.unusual'),
    videographyAvailable: i18n.t('search:facets.videographyAvailable'),
    weddingLicence: i18n.t('search:facets.weddingLicence'),
    countryHouse: i18n.t('search:facets.countryHouse'),
    countryHouseManorHouse: i18n.t('search:facets.countryHouse'),
    conferenceCentre: i18n.t('search:facets.conferenceCentre'),
    cruiseBoatYacht: i18n.t('search:facets.cruiseBoatYacht'),
    townHallRegistryOffice: i18n.t('search:facets.townHallRegistryOffice'),
    placeOfWorship: i18n.t('search:facets.placeOfWorship'),
    outdoor: i18n.t('search:facets.outdoor'),
    statelyHome: i18n.t('search:facets.statelyHome'),
    hasLateAvailability: i18n.t('search:facets.hasLateAvailability'),
    musicDj: i18n.t('search:facets.musicDj'),
    musicBand: i18n.t('search:facets.musicBand'),
    musicSolo: i18n.t('search:facets.musicSolo'),
    musicClassic: i18n.t('search:facets.musicClassic'),
    cateringStaffProvided: i18n.t('search:facets.cateringStaffProvided'),
    cateringTablewareProvided: i18n.t('search:facets.cateringTablewareProvided'),
    resort: i18n.t('search:facets.resort'),
    uniqueVenueType: i18n.t('search:facets.uniqueVenueType'),
    isCurated: i18n.t('search:facets.isCurated'),
  };

  if (isInText) {
    // These texts are different inside the facets (pills) than in the H1 or
    // HTML title
    return {
      ...defaultTexts,
      intimate: i18n.t('search:facets.intimateInText'),
      exclusiveUse: i18n.t('search:facets.exclusiveUseInText'),
    };
  }

  return defaultTexts;
};

export const getFacetFilterInText = (key: string): string => {
  const texts = getFacetFiltersTexts(true);

  return texts[key as FacetFilterKey] || '';
};

const getFacetFilters = (keys: FacetFilterKey[], withEmoji = true): FacetFilters[] => {
  const allFilters = getFacetFiltersTexts(false);

  return keys
    .map((key) => {
      const emoji = quickFiltersEmojis[key];
      let label = allFilters[key];

      if (withEmoji && emoji) {
        label = `${emoji} ${label}`;
      }

      return {
        key,
        label,
      };
    })
    .filter(({ label }) => label);
};

export const getSearchSortAndFilters = () => {
  const i18n = getI18n();

  return {
    pricingAffordable: i18n.t('search:filters.pricing.pricingAffordable'),
    pricingModerate: i18n.t('search:filters.pricing.pricingModerate'),
    pricingLuxury: i18n.t('search:filters.pricing.pricingLuxury'),
    pricingSuperLuxury: i18n.t('search:filters.pricing.pricingSuperLuxury'),
    seoScore: i18n.t('search:sorting.seoScore'),
    popular: i18n.t('search:sorting.popular'),
    new: i18n.t('search:sorting.new'),
  };
};

export default getFacetFilters;
